import { useEffect } from 'react';
export const useImpressionTracking = ({
  element,
  impressionUrl,
  viewableImpressionUrl,
  onImpression,
  onImpressionViewable
}) => {
  /**
   * UpLiftAds docs:
   *
   * Make a GET request on "impressionUrl" when an ad has been delivered to a user on the site.
   * This must be made for each and every ad displayed.
   */
  useEffect(() => {
    if (element.current && impressionUrl) {
      fetch(impressionUrl, {
        keepalive: true
      });
      onImpression?.(impressionUrl);
    }
  }, [element, impressionUrl, onImpression]);
  /**
   * UpLiftAds docs:
   *
   * "viewableImpressionUrl" provides us with a count on viewable impressions.
   * Make a GET request on this URL when the ad is in-view on the page.
   * In-view is classified as 50% of ad in-view for over 1 second.
   */
  useEffect(() => {
    if (!element.current || !viewableImpressionUrl) {
      return;
    }
    let visibilityTimeout;
    const interactionObserver = new IntersectionObserver((entries, observer) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          // Element is >=50% visible, start a 1s timer
          visibilityTimeout = window.setTimeout(() => {
            // Check again in case visibility changed during the timer
            if (entry.isIntersecting && viewableImpressionUrl) {
              fetch(viewableImpressionUrl, {
                keepalive: true
              });
              onImpressionViewable?.(viewableImpressionUrl);
              observer.disconnect();
            }
          }, 1000);
        } else {
          window.clearTimeout(visibilityTimeout);
        }
      }
    }, {
      threshold: 0.5
    });
    interactionObserver.observe(element.current);
    return () => {
      interactionObserver.disconnect();
      window.clearTimeout(visibilityTimeout);
    };
  }, [element, viewableImpressionUrl, onImpressionViewable]);
};